<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize, useWindowSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { width: windowWidth } = useWindowSize();
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings } = useUISettings();
const isSmallScreen = computed(
  () => windowWidth.value <= wootConstants.SMALL_SCREEN_BREAKPOINT
);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const openContactSidebar = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col xl:flex-row gap-3 flex-1 w-full min-w-0 px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-between w-full max-w-full min-w-0 gap-3"
    >
      <div class="flex items-center min-w-0 gap-3">
        <BackButton
          v-if="showBackButton && !isSmallScreen"
          :back-url="backButtonUrl"
          class="ltr:mr-2 rtl:ml-2"
        />
        <button
          type="button"
          class="flex items-center min-w-0 gap-3 p-0 m-0 bg-transparent border-0 outline-none"
          :class="{ 'cursor-pointer': isSmallScreen }"
          @click="isSmallScreen ? openContactSidebar() : null"
        >
          <Avatar
            :name="currentContact.name"
            :src="currentContact.thumbnail"
            :size="32"
            :status="currentContact.availability_status"
            hide-offline-status
            rounded-full
          />
          <div class="flex flex-col items-start min-w-0 overflow-hidden">
            <div class="flex items-center gap-3 p-0 m-0 max-w-full">
              <span
                v-if="isSmallScreen"
                class="text-sm font-semibold truncate leading-tight text-n-slate-12 underline-offset-2"
                style="max-width: 70vw"
              >
                {{ currentContact.name }}
              </span>
              <span
                v-else
                class="text-sm font-medium truncate leading-tight text-n-slate-12"
                style="max-width: 18rem"
              >
                {{ currentContact.name }}
              </span>
              <fluent-icon
                v-if="!isHMACVerified && !isSmallScreen"
                v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
                size="14"
                class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
                icon="warning"
              />
            </div>

            <div
              class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
            >
              <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
              <span v-if="isSnoozed" class="font-medium text-n-amber-10">
                {{ snoozedDisplayText }}
              </span>
            </div>
          </div>
        </button>
      </div>
      <MoreActions :conversation-id="currentChat.id" class="xl:hidden" />
    </div>
    <div
      class="flex items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions
        :conversation-id="currentChat.id"
        class="hidden xl:flex"
      />
    </div>
  </div>
</template>
